import request from "@/utils/request";
import { getUser } from "@/utils/auth";
import { uploadAttachment, uploadImage } from "@/api/common";

export const KEFU_USER_TYPE = {
  ADMIN: 1,
  MEMBER: 2,
};

export const KEFU_CONTENT_TYPE = {
  TEXT: 1,
  IMAGE: 2,
  FILE: 3,
  AUDIO: 4,
  PRODUCT: 10,
  ORDER: 11,
  QUOTE: 12,
  RECALLED: 99,
};

function pageEntitys(response) {
  return (response && response.data && response.data.entitys) || [];
}

function currentAdmin() {
  const user = getUser() || {};
  return {
    id: user.id || 1,
    name: user.name || "客服",
  };
}

export function getKefuConversationList(params = {}) {
  return request({
    url: "/kefu/conversation/getList",
    method: "get",
    params: {
      page: 1,
      pageSize: 100,
      ...params,
    },
  }).then(pageEntitys);
}

export function getKefuAgentList(params = {}) {
  return request({ url: "/kefu/agent/list", method: "get", params }).then(
    (response) => response.data || [],
  );
}

export function updateKefuAgentEnabled(adminUserId, enabled) {
  return request({
    url: "/kefu/agent/enabled",
    method: "post",
    data: { adminUserId, enabled },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function assignKefuConversation(conversationId, adminUserId) {
  return request({
    url: "/kefu/conversation/assign",
    method: "post",
    data: { conversationId, adminUserId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function archiveKefuConversation(conversationId) {
  return request({
    url: "/kefu/conversation/archive",
    method: "post",
    data: { conversationId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function restoreKefuConversation(conversationId) {
  return request({
    url: "/kefu/conversation/restore",
    method: "post",
    data: { conversationId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function getKefuConversation(id) {
  return request({
    url: "/kefu/conversation/getDetail",
    method: "get",
    params: { id },
  }).then((response) => response.data);
}

export function getKefuContactUsers(params = {}) {
  return request({
    url: "/kefu/conversation/users",
    method: "get",
    params: { page: 1, pageSize: 20, ...params },
  }).then(
    (response) => (response && response.data && response.data.entitys) || [],
  );
}

export function openKefuConversation(userId) {
  return request({
    url: "/kefu/conversation/open",
    method: "post",
    data: { userId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function updateKefuConversationPinned(data) {
  return request({
    url: "/kefu/conversation/pinned",
    method: "post",
    data,
    headers: {
      "Content-Type": "application/json",
    },
  }).then((response) => response.data);
}

export function deleteKefuConversation(id) {
  return request({
    url: "/kefu/conversation/delete",
    method: "delete",
    params: { id },
  }).then((response) => response.data);
}

export function takeoverKefuConversation(conversationId) {
  return request({
    url: "/kefu/conversation/takeover",
    method: "post",
    data: { conversationId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function returnKefuConversationToAi(conversationId) {
  return request({
    url: "/kefu/conversation/return-ai",
    method: "post",
    data: { conversationId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function closeKefuService(conversationId) {
  return request({
    url: "/kefu/conversation/close-service",
    method: "post",
    data: { conversationId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function getKefuMessageList(conversationId, params = {}) {
  let queryParams = {};
  if (typeof conversationId === "object" && conversationId !== null) {
    queryParams = { page: 1, pageSize: 20, ...conversationId };
  } else {
    queryParams = { conversationId, page: 1, pageSize: 20, ...params };
  }
  return request({
    url: "/kefu/message/getList",
    method: "get",
    params: queryParams,
  });
}

export function sendKefuMessage(data) {
  const admin = currentAdmin();
  return request({
    url: "/kefu/message/send",
    method: "post",
    data: {
      senderName: admin.name,
      ...data,
    },
    headers: {
      "Content-Type": "application/json",
    },
  }).then((response) => {
    if (response && response.bizcode && response.bizcode !== 100) {
      throw new Error(response.msg || "消息保存失败");
    }
    return response.data;
  });
}

export function updateKefuMessageReadStatus(conversationId) {
  return request({
    url: "/kefu/message/update-read-status",
    method: "put",
    params: { conversationId },
  }).then((response) => response.data);
}

export function recallKefuMessage(messageId) {
  return request({
    url: "/kefu/message/recall",
    method: "post",
    data: { messageId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => {
    if (response && response.bizcode && response.bizcode !== 100) {
      throw new Error(response.msg || "撤回失败");
    }
    return response.data;
  });
}

export function transcribeKefuMessage(messageId) {
  return request({
    url: "/kefu/message/transcribe",
    method: "post",
    data: { messageId },
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.data);
}

export function getKefuBrowseHistory(params = {}) {
  return request({
    url: "/kefu/user/browse-history",
    method: "get",
    params: {
      page: 1,
      pageSize: 10,
      ...params,
    },
  });
}

export function getKefuTradeOrders(params = {}) {
  return request({
    url: "/kefu/user/orders",
    method: "get",
    params: {
      page: 1,
      pageSize: 10,
      ...params,
    },
  });
}

export function buildKefuWebSocketUrl() {
  const admin = currentAdmin();
  const base =
    (window && window.__KEFU_WS_BASE__) ||
    `${window.location.protocol === "https:" ? "wss" : "ws"}://${
      window.location.host
    }/api`;
  return `${base}/ws/kefu?userType=admin&userId=${admin.id}`;
}

export async function uploadKefuImage(file) {
  const response = await uploadImage({
    file,
    type: "COMMON",
  });
  const data = (response && response.data) || {};
  const imageUrl =
    data.accessUrl || data.dbUrl || data.url || data.fileUrl || data.path;
  if (!imageUrl) {
    throw new Error(response.msg || "图片上传失败");
  }
  return imageUrl;
}

export async function uploadKefuAttachment(file, kind = "file") {
  const response = await uploadAttachment({ file, kind });
  const data = (response && response.data) || {};
  const url =
    data.accessUrl || data.dbUrl || data.url || data.fileUrl || data.path;
  if (!url) throw new Error((response && response.msg) || "附件上传失败");
  return url;
}

function kefuAiData(response) {
  if (!response || response.bizcode !== 100) {
    return Promise.reject(
      new Error((response && response.msg) || "AI客服配置请求失败"),
    );
  }
  return response.data;
}

export function getKefuAiConfig() {
  return request({ url: "/kefu/ai/config", method: "get" }).then(kefuAiData);
}

export function saveKefuAiConfig(data) {
  return request({
    url: "/kefu/ai/config",
    method: "put",
    data,
    headers: { "Content-Type": "application/json" },
  }).then(kefuAiData);
}

export function testKefuAiConnection(data) {
  return request({
    url: "/kefu/ai/connection-test",
    method: "post",
    data,
    headers: { "Content-Type": "application/json" },
  }).then(kefuAiData);
}

export function getKefuKnowledgeList(params = {}) {
  return request({ url: "/kefu/ai/knowledge", method: "get", params }).then(
    (response) => {
      if (!response || response.bizcode !== 100)
        return Promise.reject(
          new Error((response && response.msg) || "知识库请求失败"),
        );
      return response;
    },
  );
}

export function saveKefuKnowledge(data) {
  return request({
    url: "/kefu/ai/knowledge",
    method: "post",
    data,
    headers: { "Content-Type": "application/json" },
  }).then(kefuAiData);
}

export function deleteKefuKnowledge(id) {
  return request({ url: `/kefu/ai/knowledge/${id}`, method: "delete" }).then(
    kefuAiData,
  );
}

export function getKefuAiTools() {
  return request({ url: "/kefu/ai/tools", method: "get" }).then(
    (response) => kefuAiData(response) || [],
  );
}

export function saveKefuAiTools(enabledTools) {
  return request({
    url: "/kefu/ai/tools",
    method: "put",
    data: { enabledTools },
    headers: { "Content-Type": "application/json" },
  }).then((response) => kefuAiData(response) || []);
}
